import { usePlanningStatuses } from '@/queries/plan';
import { useSeenStatuses } from '@/features/plan/hooks/useSeenStatus';
import { determineDefaultPeriod } from '@/features/plan/utils/determineDefaultPeriod';
import {
  getQuarterStartAt,
  getWeekStartAt,
  getYearStartAt,
  toMidnightLocalString,
} from '@/utils/datetime';

interface SmartDefaultRoute {
  targetPath: string;
  isLoading: boolean;
}

export function useSmartDefaultRoute(): SmartDefaultRoute {
  const now = new Date();
  const yearStartAtLocal = toMidnightLocalString(getYearStartAt(now));
  const quarterStartAtLocal = toMidnightLocalString(getQuarterStartAt(now));
  const weekStartAtLocal = toMidnightLocalString(getWeekStartAt(now));

  const checks = [
    { period: 'year' as const, startAtLocal: yearStartAtLocal },
    { period: 'quarter' as const, startAtLocal: quarterStartAtLocal },
    { period: 'week' as const, startAtLocal: weekStartAtLocal },
  ];

  const {
    data: plannedStatuses,
    isLoading,
    isError,
  } = usePlanningStatuses(checks);
  const seenStatuses = useSeenStatuses(checks);

  if (isError) {
    return { targetPath: '/day', isLoading: false };
  }

  if (isLoading || !plannedStatuses) {
    return { targetPath: '/day', isLoading: true };
  }

  const statuses = {
    year: { isPlanned: plannedStatuses.year, isSeen: seenStatuses.year },
    quarter: {
      isPlanned: plannedStatuses.quarter,
      isSeen: seenStatuses.quarter,
    },
    week: { isPlanned: plannedStatuses.week, isSeen: seenStatuses.week },
  };

  const period = determineDefaultPeriod(statuses);
  return { targetPath: `/${period}`, isLoading: false };
}
